import {
  Box,
  Button,
  makeStyles,
  Modal,
  Theme,
  Typography
} from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import { AdhocAgreement, AdhocFile } from "integrity-owls-logic/src/models";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps, useHistory, withRouter } from "react-router";
import { BLUE, dateFormatText, GRAY, RED } from "../../../constants";
import { adhocAgreementsService } from "../../../services/AdhocAgreementsService";
import BasicTextField from "../../shared/BasicTextField";
import PageHeader from "../../shared/Header";

type AdhocAgreementDetailsProps = RouteComponentProps<{ id: string }>;

interface AdhocFileWithSignedUrl extends AdhocFile {
  signedUrl?: string;
}

const getModalStyle = () => {
  const top = 50;
  const left = 50;

  return {
    height: "80%",
    overflow: "scroll",
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`
  };
};

const styles = makeStyles((theme: Theme) => ({
  cancelButton: {
    color: RED + "!important",
    marginRight: "10px"
  },
  label: {
    color: GRAY,
    display: "flex",
    fontSize: "11px",
    fontWeight: "bold",
    letterSpacing: "1px",
    textTransform: "uppercase"
  },
  paper: {
    position: "absolute",
    width: 500,
    backgroundColor: theme.palette.background.paper,
    border: "none",
    borderRadius: "10px",
    outline: "none",
    marginTop: "40px",
    marginBottom: "60px",
    padding: theme.spacing(2, 4, 3)
  },
  value: {
    color: "white",
    display: "flex",
    fontSize: "20px"
  }
}));

const AgreementDetailItem = ({
  label,
  value
}: {
  label: string;
  value: string;
}) => {
  const classes = styles();
  return (
    <Box>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.value}>
        {value || "(not recorded)"}
      </Typography>
    </Box>
  );
};

export const AdhocAgreementDetailsPage: React.FC<AdhocAgreementDetailsProps> = ({
  match
}) => {
  const history = useHistory();
  const adhocAgreementId = match.params.id;
  const classes = styles();
  const [agreement, setAgreement] = useState<AdhocAgreement | null>(null);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(
    false
  );
  const [modalStyle] = useState(getModalStyle);
  const [newFileName, setNewFileName] = useState<string>("");
  const [
    fileToDelete,
    setFileToDelete
  ] = useState<AdhocFileWithSignedUrl | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e && e.target && e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      console.log("file", file);
      setUploadFile(file);
    }
  };

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fileName = newFileName;
    const file = uploadFile;

    console.log("Submit", { fileName, file });

    try {
      // loading
      console.log("Uploading file: ", { fileName, file });
      const response = await adhocAgreementsService.uploadAdhocAgreementFile({
        adhocAgreementId: parseInt(adhocAgreementId),
        fileName,
        fileTypeExtension: file ? file.type : "pdf"
      });
      console.log("response: ", response);

      const { uploadUrl } = response;

      const uploadResult = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file ? file.type : "pdf"
        }
      });

      console.log("uploadResult: ", uploadResult);
      setUploadFile(null);
      setNewFileName("");
      getData();
    } catch (err) {
      console.log("ERROR: ", err);
    } finally {
      // loading
    }
  };

  const promptDeleteFileDialogue = (file: AdhocFileWithSignedUrl) => {
    console.log("promptDeleteFileDialogue", file);
    setFileToDelete(file);
    setConfirmDeleteModalOpen(true);
  };

  const handleDeleteFile = async (fileId: number) => {
    console.log("handleDeleteFile", fileId);
    try {
      const response = await adhocAgreementsService.deleteAdhocAgreementFile({
        id: fileId
      });
      console.log("response: ", response);
      await getData();
    } catch (err) {
      console.log("ERROR: ", err);
    } finally {
      setConfirmDeleteModalOpen(false);
    }
  };

  const renderDeleteFileDialogue = () => {
    const file = fileToDelete;
    if (!file) {
      return null;
    }
    return (
      <Modal
        aria-labelledby="confirm-delete-modal-title"
        aria-describedby="confirm-delete-modal-description"
        open={confirmDeleteModalOpen}
        onClose={() => setConfirmDeleteModalOpen(false)}
      >
        <div style={modalStyle} className={classes.paper}>
          <h2 id="confirm-delete-modal-title">Delete License Request</h2>
          <p id="confirm-delete-modal-description">
            You are about to delete the following file. Are you sure you&apos;d
            like to continue?
          </p>
          <ul style={{ listStyleType: "none", padding: 0 }}>
            <li>
              <strong>{file.name}</strong>
            </li>
          </ul>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button
              className={classes.cancelButton}
              onClick={() => setConfirmDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={async () => {
                await handleDeleteFile(file.id);
              }}
            >
              Delete
            </Button>
          </Box>
        </div>
      </Modal>
    );
  };

  const getData = useCallback(async () => {
    const response = await adhocAgreementsService.getAdhocAgreement({
      id: parseInt(adhocAgreementId)
    });
    console.log("fetched adhoc agreement: ", response);
    setAgreement(response);
  }, [adhocAgreementId]);

  useEffect(() => {
    getData();
  }, []);

  if (!agreement) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <PageHeader
        title="Adhoc Agreement Details"
        rightContent={
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            style={{ cursor: "pointer" }}
            onClick={() => {
              history.push(`/adhoc-agreements/${adhocAgreementId}/edit`);
            }}
          >
            <Typography
              style={{ marginRight: "10px", color: BLUE, fontSize: 20 }}
            >
              Edit
            </Typography>
            <Edit style={{ color: BLUE }} fontSize="large" />
          </Box>
        }
      />
      <Box px={4}>
        <Box style={{ display: "flex", flexDirection: "row", gap: "75px" }}>
          <Box className="flex flex-col">
            <AgreementDetailItem
              label="Licensee Name"
              value={agreement.licenseeName || ""}
            />
            <AgreementDetailItem
              label="Song Titles"
              value={agreement.songTitles || ""}
            />
            <AgreementDetailItem
              label="Use Description"
              value={agreement.useDescription || ""}
            />
            <AgreementDetailItem label="Media" value={agreement.media || ""} />
            <AgreementDetailItem
              label="Territory"
              value={agreement.territory || ""}
            />
          </Box>
          <Box className="flex flex-col">
            <AgreementDetailItem label="Term" value={agreement.term || ""} />
            <AgreementDetailItem
              label="License Fee"
              value={agreement.licenseFee || ""}
            />
            <AgreementDetailItem
              label="Pro-Rated License Fee"
              value={agreement.proRatedLicenseFee || ""}
            />
            <AgreementDetailItem label="MFN" value={agreement.mfn || ""} />

            <AgreementDetailItem
              label="Payment Term"
              value={agreement.paymentTerm || ""}
            />
          </Box>
          <Box className="flex flex-col">
            <AgreementDetailItem
              label="Date Issued"
              value={
                agreement.dateIssued
                  ? moment(agreement.dateIssued)
                      .tz("GMT")
                      .format(dateFormatText)
                  : ""
              }
            />
            <AgreementDetailItem
              label={"Date Executed"}
              value={
                agreement.dateExecuted
                  ? moment(agreement.dateExecuted)
                      .tz("GMT")
                      .format(dateFormatText)
                  : ""
              }
            />
            <AgreementDetailItem
              label="Payment Due Date"
              value={
                agreement.paymentDueDate
                  ? moment(agreement.paymentDueDate)
                      .tz("GMT")
                      .format(dateFormatText)
                  : ""
              }
            />
            <AgreementDetailItem
              label={"Date Paid"}
              value={
                agreement.datePaid
                  ? moment(agreement.datePaid).tz("GMT").format(dateFormatText)
                  : ""
              }
            />
          </Box>
          <Box className="flex flex-col">
            <AgreementDetailItem
              label={"Project"}
              value={
                agreement && agreement.adhocProject
                  ? agreement.adhocProject.name
                  : "no project"
              }
            />
            <AgreementDetailItem
              label={"Status"}
              value={agreement.status.split("_").join(" ") || ""}
            />
            <AgreementDetailItem
              label={"Additional Provisions"}
              value={agreement.additionalProvisions || ""}
            />
            <AgreementDetailItem
              label={"Notes"}
              value={agreement.notes || ""}
            />
          </Box>
          <Box className="flex flex-col">
            <form onSubmit={e => handleUpload(e)}>
              <Box pb={1}>
                <Typography variant="overline">File Name</Typography>
                <BasicTextField
                  fieldname="fileName"
                  handleChange={e => setNewFileName(e.currentTarget.value)}
                  label="File Name"
                  value={newFileName}
                />
              </Box>

              {uploadFile ? (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="overline">{uploadFile.name}</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setUploadFile(null)}
                  >
                    Remove File
                  </Button>
                </Box>
              ) : (
                <Button variant="outlined" component="label">
                  Select File
                  <input
                    type="file"
                    style={{ display: "none" }}
                    onChange={f => handleFileUpload(f)}
                  />
                </Button>
              )}
              <Box pt={1}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!uploadFile || newFileName === ""}
                >
                  Upload File
                </Button>
              </Box>
            </form>
          </Box>
          <Box className="flex flex-col">
            <Typography variant="overline">Files</Typography>
            {(agreement.adhocFiles as AdhocFileWithSignedUrl[]).map(file => {
              return (
                <Box
                  key={file.id}
                  style={{
                    margin: "10px 0",
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center"
                  }}
                >
                  <a
                    key={file.id}
                    href={file.signedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Typography key={file.id}>{file.name}</Typography>
                  </a>
                  <Typography variant="overline">
                    | {moment(file.createdAt).tz("GMT").format(dateFormatText)}
                  </Typography>
                  <Box>
                    <CloseIcon
                      style={{ cursor: "pointer", color: RED }}
                      onClick={() => promptDeleteFileDialogue(file)}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
        {renderDeleteFileDialogue()}
      </Box>
    </div>
  );
};

export default withRouter(AdhocAgreementDetailsPage);
